import type { RemediationRule, Issue, FixResult } from '../index.js';

/**
 * WCAG 2.3.1 Three Flashes or Below Threshold (Level A) — seizure safety.
 *
 * A static DOM rule cannot measure a true flash rate (that needs frame/pixel
 * sampling), so we detect the content classes that CAN flash and freeze them:
 *   - CSS animations that loop infinitely with a cycle ≤ 333ms (> 3 per second)
 *   - <marquee> / <blink>
 *   - animated image formats (GIF / APNG) — candidate, frozen to first frame
 *   - <video autoplay> without user controls
 * The fix is deterministic (no AI): pause / freeze in place, reversible.
 * Anything frozen is still reported so the owner can fix it at source.
 */

const FROZEN_ATTR = 'data-nagisot-flash';
const FLASH_MS = 1000 / 3; // > 3 flashes per second

function cycleMs(v: string): number {
  // first value of a comma list; "0.2s" | "200ms"
  const first = v.split(',')[0]!.trim();
  const n = parseFloat(first);
  if (!Number.isFinite(n)) return Infinity;
  return first.endsWith('ms') ? n : n * 1000;
}

function isAnimatedImage(img: HTMLImageElement): boolean {
  const src = (img.currentSrc || img.getAttribute('src') || '').toLowerCase().split('?')[0]!;
  return src.endsWith('.gif') || src.endsWith('.apng');
}

export const threeFlashesRule: RemediationRule = {
  id: 'three-flashes',
  wcag: ['2.3.1'],
  tier: 'A',
  needsLLM: false,

  detect(root) {
    const issues: Issue[] = [];
    const doc = (root as Document).defaultView ? (root as Document) : (root as ShadowRoot).ownerDocument;
    const win = doc?.defaultView;

    root.querySelectorAll('marquee, blink').forEach((el, i) => {
      if (el.hasAttribute(FROZEN_ATTR)) return;
      issues.push({
        ruleId: 'three-flashes', wcag: ['2.3.1', '2.2.2'],
        selector: `${el.tagName.toLowerCase()}:nth-of-type(${i + 1})`, element: el,
        severity: 'critical', message: `<${el.tagName.toLowerCase()}> moving/blinking content`,
        payload: { kind: 'marquee-blink' },
      });
    });

    // Fast infinite CSS animations — needs layout engine (browser / extension).
    if (win && typeof win.getComputedStyle === 'function') {
      root.querySelectorAll<HTMLElement>('body *').forEach((el) => {
        if (el.hasAttribute(FROZEN_ATTR) || el.closest('#nagisot-host')) return;
        const cs = win.getComputedStyle(el);
        if (!cs.animationName || cs.animationName === 'none') return;
        const infinite = cs.animationIterationCount.split(',')[0]!.trim() === 'infinite';
        const ms = cycleMs(cs.animationDuration);
        if (infinite && ms > 0 && ms <= FLASH_MS) {
          issues.push({
            ruleId: 'three-flashes', wcag: ['2.3.1'],
            selector: el.id ? `#${el.id}` : el.tagName.toLowerCase(), element: el,
            severity: 'critical',
            message: `Infinite animation "${cs.animationName}" cycles every ${Math.round(ms)}ms (>3 flashes/sec risk)`,
            payload: { kind: 'css-animation', cycleMs: Math.round(ms), animation: cs.animationName },
          });
        }
      });
    }

    root.querySelectorAll<HTMLImageElement>('img').forEach((img, i) => {
      if (img.hasAttribute(FROZEN_ATTR) || !isAnimatedImage(img)) return;
      issues.push({
        ruleId: 'three-flashes', wcag: ['2.3.1', '2.2.2'],
        selector: `img:nth-of-type(${i + 1})`, element: img,
        severity: 'moderate', message: 'Animated image (GIF/APNG) — possible flashing, needs review',
        payload: { kind: 'animated-image', src: img.getAttribute('src') },
      });
    });

    root.querySelectorAll<HTMLVideoElement>('video[autoplay]:not([controls])').forEach((v, i) => {
      if (v.hasAttribute(FROZEN_ATTR)) return;
      issues.push({
        ruleId: 'three-flashes', wcag: ['2.3.1', '2.2.2'],
        selector: `video:nth-of-type(${i + 1})`, element: v,
        severity: 'serious', message: 'Autoplaying video without controls — flashing cannot be stopped',
        payload: { kind: 'autoplay-video' },
      });
    });

    return issues;
  },

  async fix(issue): Promise<FixResult> {
    const el = issue.element as HTMLElement | undefined;
    if (!el || el.hasAttribute(FROZEN_ATTR)) return { ok: false };
    const kind = (issue.payload?.kind as string) || '';

    if (kind === 'css-animation') {
      el.style.setProperty('animation-play-state', 'paused', 'important');
    } else if (kind === 'marquee-blink') {
      const m = el as HTMLElement & { stop?: () => void };
      if (typeof m.stop === 'function') m.stop();
      el.style.setProperty('animation', 'none', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
    } else if (kind === 'autoplay-video') {
      const v = el as HTMLVideoElement;
      v.pause(); v.removeAttribute('autoplay'); v.setAttribute('controls', '');
    } else if (kind === 'animated-image') {
      // Freeze to the current frame by painting it onto a canvas overlay.
      const img = el as HTMLImageElement;
      if (!img.complete || !img.naturalWidth) return { ok: false };
      const c = img.ownerDocument.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      try { c.getContext('2d')?.drawImage(img, 0, 0); } catch { return { ok: false }; }
      c.className = img.className;
      c.style.cssText = img.style.cssText;
      c.style.width = `${img.width}px`; c.style.height = `${img.height}px`;
      c.setAttribute('role', 'img');
      c.setAttribute('aria-label', img.alt || '');
      c.setAttribute(FROZEN_ATTR, 'canvas');
      img.style.setProperty('display', 'none', 'important');
      img.insertAdjacentElement('afterend', c);
    } else {
      return { ok: false };
    }
    el.setAttribute(FROZEN_ATTR, kind || '1');
    return { ok: true };
  },

  revert(issue) {
    const el = issue.element as HTMLElement | undefined;
    if (!el) return;
    const kind = el.getAttribute(FROZEN_ATTR);
    if (kind === 'animated-image') {
      const next = el.nextElementSibling;
      if (next?.getAttribute(FROZEN_ATTR) === 'canvas') next.remove();
      el.style.removeProperty('display');
    } else {
      el.style.removeProperty('animation-play-state');
      el.style.removeProperty('animation');
      el.style.removeProperty('visibility');
    }
    el.removeAttribute(FROZEN_ATTR);
  },
};
